import { useQueryIntegrationOnboardingSessions } from '@/hooks/query/integrations/general/onboarding-session/useQueryIntegrationOnboardingSessions';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  COMPLETED_ONBOARDING_SESSIONS_PARAMS,
  connectedAppsQueryKeys,
  type CreateIntegrationOnboardingSessionResponse,
} from '@heidi/integrations/connected-apps/contracts';
import type { EHRProvider } from '@heidi/schema/ml-scribe';
import { GET_USER } from '@heidi/workspace/contracts';
import { useEffect, useMemo } from 'react';

import { readStoredEditorVersion, type EditorVersion } from './readStoredEditorVersion';
import { selectLatestOnboardingSession } from './selectLatestOnboardingSession';

/**
 * THE frontend editor determination (html/json/markdown) — a consumer, not a
 * classifier.
 *
 * The backend resolves "which editor is this user on" in one place
 * (ml-scribe `editor_version.resolve_template_editor_version`: stored value
 * behind its rollout gate, then the flag/date chain) and serializes the
 * RESOLVED decision as `template_editor_version` on the onboarding-session
 * response. This hook reads that value off the latest completed session — it
 * never re-derives the editor from feature flags, never re-implements the
 * cohort/date windows, and holds no gate of its own. One authority, one
 * consumer.
 *
 * INTENTIONAL, AND NOT UP FOR RE-LITIGATION: the stored
 * `template_editor_version` is the authoritative signal for which editor
 * renders, and it is read for EVERY integration — not just athenahealth. The
 * backend honors a stored value with no provider check; only its flag/date
 * fallback is provider-specific. Re-introducing a client-side vendor filter or
 * a client-side flag re-derivation here is the precise defect this module
 * exists to remove: two independent classifiers that disagree, which is how a
 * clinician ends up with an editor the configuration says they should not
 * have. Please raise concerns before proposing a revert.
 *
 * - A recognized value IS the editor — including `html`, which the backend
 *   emits as its fail-closed fallthrough.
 * - An unrecognized non-null value fails closed to `html` (warned), never to
 *   a local re-derivation.
 * - A null value means the provider has no editor-version concept, an older
 *   backend, or a stale cache — `html`, and the legacy client flags decide.
 *
 * `isSettled` is false while the completed-sessions query is still
 * first-in-flight (or has errored). It is exported for send surfaces that want
 * to hold for the backend decision; no current consumer wires it, so the
 * first-flight window is intentionally unprotected. Render surfaces may render
 * the fail-closed `html` value.
 */
export type EditorVersionSource = 'backend' | 'not_applicable';

export type EditorVersionResolution = {
  version: EditorVersion;
  source: EditorVersionSource;
  /** False while the completed-sessions query is still first-in-flight (or
   *  has errored). Exported for send surfaces that want to hold; currently
   *  unconsumed — see the module docblock. */
  isSettled: boolean;
  /**
   * The wire carried a recognized, non-null `template_editor_version`, so
   * `version` is the backend's concrete decision rather than this module's
   * fail-closed fallthrough.
   *
   * The core rule keys off exactly this: a non-null resolved value IS the
   * editor and no client feature flag gets a vote. When it is false (a null or
   * absent value, an unrecognized value, or a query error) the legacy client
   * flags decide, byte-identical to the behaviour before this field existed.
   *
   * Note this is NOT "an administrator set this value". The backend resolves
   * stored-value OR flag/date chain and serializes the winner, so the frontend
   * cannot (and must not) distinguish the two — it only knows whether a
   * concrete decision arrived.
   */
  isAuthoritative: boolean;
};

/**
 * Warn on an unrecognized wire value. Kept OUT of `resolveFromSession` (which
 * runs inside `useMemo` — a computation React treats as pure, and StrictMode
 * double-invokes) so the side effect fires from `useEffect` in the hook and
 * inline in the imperative twin instead. Logs the shape (type + length),
 * never the raw wire value — telemetry PHI/PII safety.
 *
 * Dedupe: the imperative twin runs on sync paths that fire repeatedly
 * (`copyHelpers` calls `isEnableBlockEditorV1` at three sites, plus
 * `resolveDocumentContentType`, `_stream-response.ts`, and
 * `isAthenaMarkdownCohortEnabled`), and StrictMode double-invokes the root
 * mount's effect. One warn per distinct unrecognized value per process
 * lifetime bounds the volume — the scenario that matters is the backend
 * widening the enum ahead of a FE regen, which would otherwise warn on every
 * imperative call indefinitely. Keyed on the raw value for strings (a closed
 * enum in practice) and on `typeof` for non-strings; the value stays in
 * memory, never reaches telemetry.
 */
const unrecognizedWarned = new Set<string>();
const unrecognizedWarnKey = (raw: unknown): string =>
  typeof raw === 'string' ? raw : `<${typeof raw}>`;

export const warnIfUnrecognized = (
  session: CreateIntegrationOnboardingSessionResponse | undefined
): void => {
  const read = readStoredEditorVersion(session);
  if (read.kind !== 'unrecognized') return;
  const key = unrecognizedWarnKey(read.raw);
  if (unrecognizedWarned.has(key)) return;
  unrecognizedWarned.add(key);
  logger.warn('[editor-version] unrecognized template_editor_version', {
    type: typeof read.raw,
    length: typeof read.raw === 'string' ? read.raw.length : undefined,
  });
};

/** @internal Test-only: reset the module-level warn dedupe. */
export const __resetUnrecognizedWarnDedupe = (): void => {
  unrecognizedWarned.clear();
};

const resolveFromSession = (
  session: CreateIntegrationOnboardingSessionResponse | undefined,
  isSettled: boolean
): EditorVersionResolution => {
  const read = readStoredEditorVersion(session);
  if (read.kind === 'pinned') {
    return {
      version: read.version,
      source: 'backend',
      isSettled: true,
      isAuthoritative: true,
    };
  }
  if (read.kind === 'unrecognized') {
    // Fail closed to html. The warn is a side effect — see `warnIfUnrecognized`.
    return {
      version: 'html',
      source: 'backend',
      isSettled: true,
      isAuthoritative: false,
    };
  }
  if (session) {
    // Null on a session we found: the provider has no editor-version concept,
    // or an older backend / stale cache. Fail closed to html and let the
    // legacy client flags decide.
    return { version: 'html', source: 'backend', isSettled, isAuthoritative: false };
  }
  // Settled cache with no completed session at all. `not_applicable` is
  // reserved for a SETTLED "no onboarding" decision; while unsettled (the
  // skip-token / in-flight window) the honest source is `backend` — "no
  // decision yet" — not the value that means "decided: no Athena onboarding".
  return {
    version: 'html',
    source: isSettled ? 'not_applicable' : 'backend',
    isSettled,
    isAuthoritative: false,
  };
};

export const useResolvedEditorVersion = (
  ehrProvider?: EHRProvider
): EditorVersionResolution => {
  // Mounted for every user regardless of provider: the backend value is the
  // determination and it is not vendor-scoped, so there is no provider or flag
  // state under which the read is unnecessary. Shared/deduped cache entry
  // (1h staleTime) — one request per app load, deduped with every other
  // completed-sessions consumer, so this costs no extra network.
  const {
    data: session,
    isPending,
    isError,
  } = useQueryIntegrationOnboardingSessions({
    params: COMPLETED_ONBOARDING_SESSIONS_PARAMS,
    select: (data) => selectLatestOnboardingSession(data.sessions, ehrProvider),
  });

  const resolution = useMemo((): EditorVersionResolution => {
    // A query error with NO retained data is "no decision exists": fail closed
    // to html, source 'backend' (not 'not_applicable'), isSettled false so a
    // send surface that wires it holds rather than routing to the legacy
    // uploader path on a transient sessions-endpoint blip. Constant message
    // (no response body; telemetry PHI/PII safety).
    //
    // A query error WITH retained data is a different state: v5 status
    // 'error' coexists with the last successful fetch's data (what
    // isRefetchError, `isError && dataUpdatedAt > 0`, names), so a
    // staleTime-expired refetch that fails still holds the prior pin in cache.
    // Discarding it would silently demote a pinned markdown user to the legacy
    // HTML editor + legacy /ehr/uploader push path on a wifi blip - the exact
    // downgrade the earlier isError note asked to prevent - and would split
    // the seam: the imperative twin reads via getQueryData (no error concept),
    // so it would keep returning markdown while the hook returned html. Two
    // answers for one user is the defect this module exists to remove. Keep a
    // retained pin winning; only the no-data error is unsettled.
    if (isError && !session) {
      return {
        version: 'html',
        source: 'backend',
        isSettled: false,
        isAuthoritative: false,
      };
    }
    // `isPending` is true for BOTH the skip-token window (identity terms not
    // resolved — `queryFn: skipToken`, v5 reports `status: 'pending'` /
    // `fetchStatus: 'idle'`) and the in-flight fetch. `isLoading` is only the
    // latter (`isPending && isFetching`), so it is false during skip-token and
    // would report a settled `not_applicable` for an Athena user before
    // identity resolves. `!isPending` covers both windows with no new state.
    // `!isError` keeps a retained pin reporting unsettled while a refetch is
    // failing (the refetch could still change it) without discarding it - see
    // the `isError && !session` branch above.
    return resolveFromSession(session, !isPending && !isError);
  }, [session, isPending, isError]);

  // The only side effect kept in the shared resolver: a single classified
  // log line on a query error. Kept OUT of the `useMemo` above (StrictMode
  // double-invokes memos; a log there would fire twice / on a discarded pass)
  // and keyed on `isError` so it fires once per error transition, not per
  // recompute. The `sectional_editor_version` dimension stamp and the
  // `warnIfUnrecognized` call live in the single `EditorVersionPrefetch` root
  // mount — one writer, user scope — so a session-scoped caller can't blank a
  // user-scoped dimension. See `EditorVersionPrefetch`.
  useEffect(() => {
    if (!isError) return;
    logger.info('[editor-version] completed-sessions query failed, unsettled');
  }, [isError]);

  return resolution;
};

/**
 * Imperative counterpart for non-React contexts (API helpers, copy helpers,
 * note generation). Stays sync so callers don't have to await; self-warms
 * the shared completed-sessions cache (fire-and-forget `ensureQueryData`) so
 * a cold cache costs nothing extra on later calls. On a cold cache the first
 * call returns the fail-closed `html` with `isSettled: false`; once the
 * prefetch settles, every subsequent call sees the backend's decision.
 */
export const resolveEditorVersion = (
  ehrProvider?: EHRProvider
): EditorVersionResolution => {
  void (async () => {
    // Identity gate: the hook path withholds the request behind `skipToken`
    // until `isAuthenticated && region && user` resolve. This imperative warm
    // supplies its own `queryFn` against the same cache entry with no such
    // gate, so without it an early call (generation / clipboard, before auth)
    // can fire the request, 401, and poison the shared entry with `isError` —
    // every hook observer then fail-closes to html. Skip the warm until the
    // user term is in the cache (a populated `[GET_USER]` implies auth
    // resolved); the root `EditorVersionPrefetch` is the primary warmer once
    // identity lands, so skipping here only delays a belt-and-suspenders warm.
    const user = reactQueryClient.getQueryData<{ kinde_user_id?: string }>([GET_USER]);
    if (!user?.kinde_user_id) return;
    try {
      // Dynamic import avoids a top-level circular dependency between this
      // module chain and `scribeAPIV2.ts` (which imports
      // `isEnableBlockEditorV1` → the resolver → this module).
      const { getIntegrationOnboardingSessions } =
        await import('@/pages/api/scribeAPIV2');
      await reactQueryClient.ensureQueryData({
        queryKey: connectedAppsQueryKeys.completedOnboardingSessions(),
        queryFn: () =>
          getIntegrationOnboardingSessions({
            params: COMPLETED_ONBOARDING_SESSIONS_PARAMS,
          }),
      });
    } catch (err) {
      // Network / auth failure — log a classification (not the response or
      // error message; telemetry PHI/PII safety). The next call retries via
      // the same path; per-call retries are bounded by React Query's retry
      // config, and the ScribeLayout prefetch is the primary warmer.
      logger.info('[editor-version] prefetch failed, will retry on next call', {
        errorType: err instanceof Error ? err.name : 'unknown',
      });
    }
  })();

  const cached = reactQueryClient.getQueryData<{
    sessions: CreateIntegrationOnboardingSessionResponse[];
  }>(connectedAppsQueryKeys.completedOnboardingSessions());
  const session = cached
    ? selectLatestOnboardingSession(cached.sessions, ehrProvider)
    : undefined;

  // The imperative twin is a sync (non-render) path, so the side-effecting
  // warn lives here directly rather than in a `useEffect` (see the hook).
  warnIfUnrecognized(session);

  // Cold cache means a later call may resolve differently once the prefetch
  // above lands — reported through isSettled.
  return resolveFromSession(session, cached !== undefined);
};

/** Thin boolean shims so consumers migrate incrementally without re-deriving
 *  the editor. Derived from one enum, so "json AND markdown both true" is
 *  unrepresentable. */
export const useIsJsonSectionalEditor = (ehrProvider?: EHRProvider): boolean =>
  useResolvedEditorVersion(ehrProvider).version === 'json';

export const useIsMarkdownSectionalEditor = (ehrProvider?: EHRProvider): boolean =>
  useResolvedEditorVersion(ehrProvider).version === 'markdown';
